import { useCallback, useEffect, useState } from 'react';
import {
  completeOnboarding,
  getOnboardingState,
  saveOnboardingCard,
  type OnboardingCardPatch,
  type OnboardingState,
} from './api';

/**
 * E7 FRONT DOOR — the data hook for the 7-card guided flow, ported from
 * apps/web/src/lib/useOnboarding.ts (LANE E7-PORT-THROUGHLINE). Thin: it only
 * wires GET /api/profile/onboarding, PUT /api/profile (per-card saves), and
 * POST /api/profile/onboarding/complete to React state. All step-sequencing/
 * preview-mode/gate logic lives in components/FrontDoorCards.tsx and
 * lib/frontDoorCards.ts / lib/useFrontDoorGate.ts — this hook never
 * re-derives eligibility itself (FE renders served truth).
 */
export function useFrontDoorCards() {
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const next = await getOnboardingState();
      setState(next);
      return next;
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Each card saves IMMEDIATELY (leaving mid-flow loses nothing). Merges the
  // freshly-returned profile into local state so the next card (and a
  // reload) sees it without a full re-fetch.
  const saveCard = useCallback(async (patch: OnboardingCardPatch) => {
    setSaving(true);
    setSaveError(null);
    try {
      const { profile } = await saveOnboardingCard(patch);
      setState((prev) => (prev ? { ...prev, profile } : prev));
      return profile;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setSaveError(message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const complete = useCallback(async () => {
    const { onboardedAt } = await completeOnboarding();
    setState((prev) => (prev ? { ...prev, profile: { ...prev.profile, onboardedAt } } : prev));
    return onboardedAt;
  }, []);

  return { state, loading, loadError, saving, saveError, refresh, saveCard, complete };
}
